import {
  OrderState,
  ProBuyerProjectPriceKind,
  ProBuyerProjectTotalsFragment,
  ReservationStatus,
} from "@/Apollo/schema";
import {
  PRO_PROJECTS_NEW_LABEL_DAYS,
  PRO_PROJECT_ERROR_CODE,
  PRO_PROJECT_NAME_MAX_LENGTH,
  ProProjectsCopyKey,
} from "@/features/pro-buyer-projects/static/pro-buyer-projects.static";
import { ApolloError } from "@apollo/client";

/** Same normalisation as the gateway, so the counter and the server agree. */
export const normalizeProjectName = (raw: string) =>
  raw.trim().replace(/\s+/g, " ");

/** The copy key for a name the gateway would refuse on length, or null when it may be sent. */
export const getProjectNameLengthErrorKey = (
  raw: string
): ProProjectsCopyKey | null => {
  const name = normalizeProjectName(raw);

  if (name.length === 0) {
    return "errorNameEmpty";
  }

  return [...name].length > PRO_PROJECT_NAME_MAX_LENGTH
    ? "errorNameInvalid"
    : null;
};

export const isProjectNameValid = (raw: string) =>
  getProjectNameLengthErrorKey(raw) === null;

const getErrorCode = (error: unknown): string | undefined => {
  if (!(error instanceof ApolloError)) {
    return undefined;
  }

  const code = error.graphQLErrors[0]?.extensions?.code;

  return typeof code === "string" ? code : undefined;
};

/** The copy key that explains a failed create or rename. */
export const getProjectNameErrorCopyKey = (
  error: unknown
): ProProjectsCopyKey => {
  switch (getErrorCode(error)) {
    case PRO_PROJECT_ERROR_CODE.nameInvalid:
      return "errorNameInvalid";
    case PRO_PROJECT_ERROR_CODE.nameTaken:
      return "errorNameTaken";
    case PRO_PROJECT_ERROR_CODE.nameContactDetails:
      return "errorNameContact";
    default:
      return "errorGeneric";
  }
};

type Totals = readonly Pick<
  ProBuyerProjectTotalsFragment,
  "spent" | "proBuyerSpent" | "saved"
>[];

/** Whole percent, or null when there is nothing to divide by or the amounts are in several currencies. */
const percent = (part: number, whole: number) =>
  whole > 0 ? Math.round((part / whole) * 100) : null;

/** "Met Pro Buyer-prijs": share of spent bought at a Pro Buyer price. */
export const getProBuyerSharePct = (totals: Totals): number | null => {
  const total = totals.length === 1 ? totals[0] : undefined;

  if (!total) {
    return null;
  }

  return percent(total.proBuyerSpent.amount, total.spent.amount);
};

/**
 * "Bespaard met Pro Buyer": average discount on the Pro Buyer-price purchases,
 * saved / (paid at Pro Buyer price + saved), i.e. against the regular price.
 */
export const getAverageDiscountPct = (totals: Totals): number | null => {
  const total = totals.length === 1 ? totals[0] : undefined;

  if (!total) {
    return null;
  }

  return percent(
    total.saved.amount,
    total.proBuyerSpent.amount + total.saved.amount
  );
};

/** WHO-5959 F10: the saved amount is green only when something was saved. */
export const hasSavings = (totals: Totals): boolean =>
  totals.some(total => total.saved.amount > 0);

export const PRO_PROJECT_PURCHASE_FILTER = {
  all: "all",
  proBuyer: "pro_buyer",
  noDiscount: "no_discount",
} as const;

export type ProProjectPurchaseFilter =
  (typeof PRO_PROJECT_PURCHASE_FILTER)[keyof typeof PRO_PROJECT_PURCHASE_FILTER];

/** Spec S3 filter: Alles / Met Pro Buyer-prijs / Zonder korting. */
export const filterProjectPurchases = <
  T extends { priceKind: ProBuyerProjectPriceKind },
>(
  purchases: readonly T[],
  filter: ProProjectPurchaseFilter
): T[] => {
  switch (filter) {
    case PRO_PROJECT_PURCHASE_FILTER.proBuyer:
      return purchases.filter(
        purchase => purchase.priceKind === ProBuyerProjectPriceKind.ProBuyer
      );
    case PRO_PROJECT_PURCHASE_FILTER.noDiscount:
      return purchases.filter(
        purchase => purchase.priceKind !== ProBuyerProjectPriceKind.ProBuyer
      );
    default:
      return [...purchases];
  }
};

/** Spec S3 price label key. */
export const getPriceKindCopyKey = (
  kind: ProBuyerProjectPriceKind
): ProProjectsCopyKey => {
  switch (kind) {
    case ProBuyerProjectPriceKind.ProBuyer:
      return "labelProBuyer";
    case ProBuyerProjectPriceKind.Bid:
      return "labelBid";
    default:
      return "labelRegular";
  }
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Spec S1: the "Nieuw" pill shows for 30 days after the item first appeared for this account. */
export const isProProjectsNewLabelVisible = (
  firstSeenAt: number,
  now: number
) => now - firstSeenAt < PRO_PROJECTS_NEW_LABEL_DAYS * DAY_MS;

/** Decision 2a: cancelled and expired purchases stay listed with a status label and never count. */
export const isEndedOrderState = (state: OrderState) =>
  state === OrderState.Canceled || state === OrderState.Expired;

/** Characters Windows and macOS refuse in a file name. */
const UNSAFE_FILE_NAME_CHARACTERS = /[\\/:*?"<>|]/g;

/**
 * WHO-5959 6d, spec S10: "{project} facturen.zip", with the part number when
 * the project is split into zips of 50. The project name is the buyer's own
 * text, so characters a file system refuses become "_".
 */
export const getInvoicesZipFileName = (
  baseName: string,
  part: number | null
) => {
  const safe = baseName.replace(UNSAFE_FILE_NAME_CHARACTERS, "_").trim();

  return `${safe}${part === null ? "" : ` ${part}`}.zip`;
};

type ReservationForSummary = {
  status: ReservationStatus;
  reservedUntil?: string | null;
  fixedPrice?: { amount: number; currency: string } | null;
};

/**
 * WHO-5959 6e, spec S3: the Gereserveerd figure of a project. The amount is
 * the fixed price of the running reservations (one entry per currency);
 * `hoursLeft` is the shortest clock among them; requests have no price yet.
 */
export const summarizeProjectReservations = (
  reservations: readonly ReservationForSummary[],
  now = new Date()
) => {
  const accepted = reservations.filter(
    reservation => reservation.status === ReservationStatus.Accepted
  );
  const amounts = new Map<string, number>();

  accepted.forEach(reservation => {
    if (reservation.fixedPrice) {
      const { currency, amount } = reservation.fixedPrice;
      amounts.set(currency, (amounts.get(currency) ?? 0) + amount);
    }
  });

  const ends = accepted
    .map(reservation =>
      reservation.reservedUntil
        ? new Date(reservation.reservedUntil).getTime()
        : NaN
    )
    .filter(time => !Number.isNaN(time));
  const soonest = ends.length > 0 ? Math.min(...ends) : null;

  return {
    acceptedCount: accepted.length,
    requestedCount: reservations.filter(
      reservation => reservation.status === ReservationStatus.Requested
    ).length,
    amounts: [...amounts.entries()].map(([currency, amount]) => ({
      currency,
      amount: Math.round(amount * 100) / 100,
    })),
    hoursLeft:
      soonest === null
        ? null
        : Math.max(0, Math.floor((soonest - now.getTime()) / 3_600_000)),
  };
};

/** The moment a live row ends: the reservation's end, or the seller's answer deadline for a request. */
export const reservationDeadline = (reservation: {
  status: ReservationStatus | string;
  reservedUntil?: string | null;
  requestExpiresAt: string;
}) =>
  reservation.status === ReservationStatus.Accepted && reservation.reservedUntil
    ? reservation.reservedUntil
    : reservation.requestExpiresAt;

/** Shortest time left first (spec S9; the gateway sends them in this order too). */
export const sortByTimeLeft = <
  TReservation extends Parameters<typeof reservationDeadline>[0],
>(
  reservations: readonly TReservation[]
) =>
  [...reservations].sort(
    (left, right) =>
      new Date(reservationDeadline(left)).getTime() -
      new Date(reservationDeadline(right)).getTime()
  );
